import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { usersState } from './initialState';
import {
  changePassword,
  checkBeforeRegisterUser,
  fetchSupervisorInfo,
  login,
  loginManager,
  loginVerify,
  logout,
  refreshTokenThunk,
  registerNewUser,
  resetPassword,
} from '~/store/users/thunks.ts';
import {
  clientLoginFulfilled,
  managerLoginFulfilled,
  parseUserByToken,
  refreshFulfilled,
  registerUsersFulfilled,
  supervisorInfoFulfilled,
  usersError,
  usersFulfilled,
  usersPending,
  logoutFulfilled,
  loginMfaFulfilled,
} from '~/store/users/actions.ts';
import { ClientRole } from '~/constants/clientTypes';
import { FetchStatus } from '~/store/covenants/types.ts';

const usersSlice = createSlice({
  name: 'users',
  initialState: usersState,
  reducers: {
    clearUsersState() {
      return {
        ...usersState,
      };
    },
    setUsersError(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.loading = false;
    },
    setUserByUrlToken(state, action: PayloadAction<ClientRole | undefined>) {
      parseUserByToken(state);
      if (action.payload === ClientRole.ASSET_MANAGER) {
        state.isAssetManager = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.rejected, usersError)
      .addCase(login.fulfilled, loginMfaFulfilled)
      .addCase(login.pending, usersPending)

      .addCase(loginVerify.rejected, usersError)
      .addCase(loginVerify.fulfilled, clientLoginFulfilled)
      .addCase(loginVerify.pending, usersPending)

      .addCase(changePassword.fulfilled, usersFulfilled)
      .addCase(changePassword.pending, usersPending)
      .addCase(changePassword.rejected, usersError)

      .addCase(resetPassword.rejected, usersError)
      .addCase(resetPassword.fulfilled, usersFulfilled)
      .addCase(resetPassword.pending, usersPending)

      .addCase(logout.rejected, usersError)
      .addCase(logout.fulfilled, logoutFulfilled)
      .addCase(logout.pending, usersPending)

      .addCase(checkBeforeRegisterUser.pending, usersPending)
      .addCase(checkBeforeRegisterUser.rejected, usersError)

      .addCase(registerNewUser.rejected, usersError)
      .addCase(registerNewUser.fulfilled, registerUsersFulfilled)
      .addCase(registerNewUser.pending, usersPending)

      .addCase(loginManager.rejected, usersError)
      .addCase(loginManager.fulfilled, managerLoginFulfilled)
      .addCase(loginManager.pending, usersPending)

      .addCase(refreshTokenThunk.pending, (state) => {
        state.loading = true;
        state.isInitialized = true;
        state.error = '';
      })
      .addCase(refreshTokenThunk.fulfilled, refreshFulfilled)
      .addCase(refreshTokenThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message ?? 'Unknown error';
      })
      .addCase(fetchSupervisorInfo.pending, (state) => {
        state.supervisorInfo.status = FetchStatus.Loading;
        state.supervisorInfo.error = null;
      })
      .addCase(fetchSupervisorInfo.fulfilled, supervisorInfoFulfilled)
      .addCase(fetchSupervisorInfo.rejected, (state, action) => {
        state.supervisorInfo.status = FetchStatus.Error;
        state.supervisorInfo.error = action.payload ?? null;
      });
  },
});

export const { clearUsersState, setUsersError, setUserByUrlToken } = usersSlice.actions;

export default usersSlice.reducer;
